import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Form, message, Typography } from 'antd';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { FormDividerSpace } from 'client/ui/space/FormDividerSpace';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { useDispatch } from 'react-redux';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { AuthActions } from 'client/components/auth/AuthModule';
import { useTranslation } from 'react-i18next';
import { SoapLdapApi } from 'client/components/schema/users/SoapLdapApi';
import {
  IEmployeeCreateAccessTokenDto,
  employeeCreateAccessTokenValidator
} from 'common/dto/EmployeeDto';

interface Params {
  id: string;
}

interface LocationState {
  from?: string;
}

export interface EmployeeFrontLoginPageProps
  extends RouteComponentProps<Params, any, LocationState> {}

/**
 * Pagina di Login di un utente con email e password (senza SPID)
 */
export function EmployeeFrontLoginPage(props: EmployeeFrontLoginPageProps) {
  const [login] = useApiMutation(SoapLdapApi.login, {});
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const fromUrl = props.location.state?.from;

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: '/utente/login',
    title: 'Login'
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.login.employee-breadcrumb', 'Login dipendenti')
  });

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };
  return (
    <FrontLayout>
      <Formik<IEmployeeCreateAccessTokenDto>
        enableReinitialize
        initialValues={employeeCreateAccessTokenValidator.cast()}
        validate={schemaToValidator(employeeCreateAccessTokenValidator)}
        onSubmit={async values => {
          try {
            const result = await login({ data: values });
            dispatch(AuthActions.loginSucceed('jwt', result.data.accessToken));

            // Se l'utente viene da una pagina che non sia di login ecc., lo
            // reindirizziamo lì.
            if (fromUrl && !fromUrl.startsWith('/utente')) {
              console.log(`[Redirect dopo login] ${fromUrl}`);
              props.history.push(fromUrl);
            }
          } catch (e) {
            console.error({ error: e });
            if (e.response.status !== 400) {
              // Non mostriamo questo error se è un problema di credenziali
              message.error(
                `Si è verificato un errore durante il login. Riprovare.`
              );
            }
          }
        }}
      >
        {({ isSubmitting }) => (
          <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
            <Card>
              {/* Form Utente */}
              <FormikForm {...layout}>
                <FormDividerSpace
                  title={t(
                    'user.login.employee-title',
                    'Effettua l’accesso dipendenti'
                  )}
                  layout={layout}
                  style={{ marginTop: 0 }}
                />

                <TextInputItem
                  label={t('user.login.employee-username', 'Nome utente')}
                  name="username"
                  prefix={<UserOutlined style={{ color: '#d9d9d9' }} />}
                  placeholder={t('user.login.employee-username', 'Nome utente')}
                />
                <TextInputItem
                  label="Password"
                  name="password"
                  type="password"
                  prefix={<LockOutlined style={{ color: '#d9d9d9' }} />}
                  placeholder="Password"
                />
                <Form.Item {...tailLayout}>
                  <Button
                    loading={isSubmitting}
                    htmlType="submit"
                    type="primary"
                    style={{ width: '100%' }}
                  >
                    {t('user.login.login-button', 'Accedi')}
                  </Button>
                </Form.Item>
              </FormikForm>
            </Card>
          </div>
        )}
      </Formik>
    </FrontLayout>
  );
}
